import { Box, Paper } from '@mui/material';
import {
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { GoogleLogin } from '@react-oauth/google';
import { FC, useEffect } from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import logoPrisma from '../../assets/svg/logo-prisma-small.svg';
import logoOne from '../../assets/svg/one-web.svg';
import { paths } from '../../routing';
import { auth, loginWithAzureIdToken } from '../../utils/auth';
import {
  getAzureIdTokenFromRedirect,
  hasAzureOAuthReturn,
  isAzureLoginConfigured,
  startAzureLogin,
} from '../../utils/azureAuth';
import { AzureLoginButton } from './AzureLoginButton';

const LOGIN_BACKGROUND =
  "url('https://one.img.pmdstatic.net/fit/http.3A.2F.2Frec2-bone-image.2Es3-website-eu-west-1.2Eamazonaws.2Ecom.2Fcap.2F2020.2F12.2F11.2F0db5a9b3-2a0b-4dbe-83e9-562cf9b5a02a.2Ejpeg/800x450/quality/100/crop-from/top/transformation.jpg')";

interface LoginLocationState {
  from?: string | { pathName?: string };
}

const getPathAfterLogin = (from?: LoginLocationState['from']) => {
  if (
    !from ||
    from === paths.LOGIN ||
    (typeof from !== 'string' && from.pathName === paths.LOGIN)
  ) {
    return paths.CAMPAIGN_LIST;
  }
  return typeof from === 'string' ? from : from.pathName || paths.CAMPAIGN_LIST;
};

export const LoginPage: FC<RouteComponentProps> = ({ history, location }) => {
  const { pushNotification } = useNotification();
  const isAuthenticated = auth.isAuthenticated();

  useEffect(() => {
    if (auth.isAuthenticated()) {
      history.replace(paths.CAMPAIGN_LIST);
    }
  }, [isAuthenticated, history]);

  useEffect(() => {
    if (!hasAzureOAuthReturn()) {
      return;
    }

    const completeAzureLogin = async () => {
      try {
        const idToken = await getAzureIdTokenFromRedirect();
        if (!idToken) {
          throw new Error('Erreur de connexion');
        }
        const jwt = await loginWithAzureIdToken(idToken);
        if (!jwt) {
          throw new Error('Erreur de connexion');
        }
        history.replace(paths.CAMPAIGN_LIST);
      } catch (error) {
        pushNotification({
          message:
            error instanceof Error ? error.message : 'Erreur de connexion',
          type: NotificationTypeEnum.error,
        });
      }
    };

    void completeAzureLogin();
  }, [history, pushNotification]);

  if (isAuthenticated) {
    return <Redirect to={paths.CAMPAIGN_LIST} />;
  }

  const loginWithIdToken = async (idToken?: string) => {
    try {
      if (!idToken) {
        throw new Error('Erreur de connexion');
      }
      const jwt = await auth.login(idToken);
      if (!jwt) {
        throw new Error('Erreur de connexion');
      }
      const stateFrom = location.state as LoginLocationState | undefined;
      history.push(getPathAfterLogin(stateFrom?.from));
    } catch (error) {
      pushNotification({
        message: error instanceof Error ? error.message : 'Erreur de connexion',
        type: NotificationTypeEnum.error,
      });
    }
  };

  const onAzureClick = async () => {
    try {
      await startAzureLogin();
    } catch (error) {
      pushNotification({
        message: error instanceof Error ? error.message : 'Erreur de connexion',
        type: NotificationTypeEnum.error,
      });
    }
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        background: LOGIN_BACKGROUND,
        backgroundSize: 'cover',
        display: 'flex',
        height: '100vh',
        justifyContent: 'center',
        width: '100vw',
      }}
    >
      <Paper
        sx={{
          alignItems: 'center',
          backgroundColor: 'common.white',
          display: 'flex',
          flexDirection: 'column',
          padding: '40px',
        }}
      >
        <img src={logoPrisma} alt="Prisma Logo" width={206} />
        <Box
          component="img"
          src={logoOne}
          alt="One Logo"
          sx={{ height: 50, marginBottom: '5px', marginTop: '30px' }}
        />
        <Box
          sx={{
            color: 'common.black',
            fontSize: 15,
            fontWeight: 100,
            letterSpacing: 1,
            marginBottom: '30px',
          }}
        >
          SUITE ONE
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <GoogleLogin
            onSuccess={(response) => loginWithIdToken(response.credential)}
            onError={() => {
              pushNotification({
                message: 'Erreur de connexion',
                type: NotificationTypeEnum.error,
              });
            }}
            width="280"
          />
          {isAzureLoginConfigured() && (
            <AzureLoginButton onClick={onAzureClick} />
          )}
        </Box>
      </Paper>
    </Box>
  );
};
